import axios from '../axios';

// ---------------------------------------------------------------------------
// Catálogo de permissões (IAM) — service da fronteira (FC1 §A.7).
//
// Contrato Gen3 desde o handoff 0111 (2026-09-15): JSend em tudo —
// `GET /permissions` → `data.permissions[]`, `GET /permissions/:id` →
// `data.permission`, `PUT /permissions/:id` (curadoria de `is_sensitive`,
// RBAC `iam:permission:update`) → `data.permission`. Os leitores continuam
// defensivos DE PROPÓSITO: o backend sobe primeiro (back-first), mas até o
// deploy o dev/prod antigo responde array cru — as duas formas convivem aqui
// e em nenhuma tela.
//
// `is_sensitive` sai como boolean no contrato; o normalizador aceita 0/1
// legado. Campo AUSENTE (pré-0111) → `isSensitive: null`, e
// `catalogSupportsSensitivity` devolve false — as telas escondem a curadoria
// em vez de oferecer uma escrita que tomaria 404.
// ---------------------------------------------------------------------------

const ACTION_CONFIG = { _skipForbiddenRedirect: true };

function normalizePermission(perm) {
   return {
      id: perm.id,
      module: perm.module ?? 'other',
      submodule: perm.submodule ?? null,
      resource: perm.resource ?? '',
      action: perm.action ?? '',
      key: perm.key ?? `${perm.module}:${perm.resource}:${perm.action}`,
      label: perm.label ?? '',
      // null = backend ainda não expõe o campo (pré-0111)
      isSensitive:
         perm.is_sensitive == null ? null : Boolean(Number(perm.is_sensitive)),
   };
}

/**
 * Catálogo. Filtros do contrato 0111 (§3.1) — todos contains, camel→snake na
 * margem; só vai para a query o que não está vazio. `id` NÃO é filtro do
 * backend (a tela que precisar filtra client-side). Sem paginação de
 * propósito: o catálogo é fechado por código (~120 chaves).
 *
 * @param {object} [filters]
 * @param {string} [filters.module]
 * @param {string} [filters.submodule]
 * @param {string} [filters.resource]
 * @param {string} [filters.action]
 * @param {string} [filters.key]
 * @param {string} [filters.label]
 * @param {boolean} [filters.isSensitive]
 */
export async function listPermissions(filters = {}) {
   const params = {};
   ['module', 'submodule', 'resource', 'action', 'key', 'label'].forEach(
      (name) => {
         if (filters[name]) params[name] = filters[name];
      }
   );
   if (filters.isSensitive !== undefined && filters.isSensitive !== '') {
      params.is_sensitive = String(Boolean(filters.isSensitive));
   }

   const response = await axios.get('/permissions', { params });
   const body = response?.data;

   const list = (() => {
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.permissions)) return body.permissions;
      if (Array.isArray(body?.data?.permissions)) return body.data.permissions;
      if (Array.isArray(body?.data)) return body.data;
      return [];
   })();

   return list.map(normalizePermission);
}

/**
 * Detalhe: a permissão + quem a tem (`roles[].users`, contrato 0111 §3.3 —
 * o alias cru `Users` do shape antigo é normalizado aqui para as duas formas
 * conviverem durante o deploy).
 */
export async function getPermission(permissionId) {
   const response = await axios.get(`/permissions/${permissionId}`);
   const body = response?.data;
   const raw = body?.data?.permission ?? body?.permission ?? body ?? {};

   return {
      ...normalizePermission(raw),
      roles: (Array.isArray(raw.roles) ? raw.roles : []).map((role) => ({
         ...role,
         users: role.users ?? role.Users ?? [],
      })),
   };
}

/** O backend já expõe `is_sensitive`? (feature-detect do contrato 0111) */
export function catalogSupportsSensitivity(permissions) {
   return permissions.some((perm) => perm.isSensitive !== null);
}

/**
 * Curadoria: marca/desmarca a permissão como sensível — muda o METADADO do
 * catálogo (vale para todos os perfis), não a concessão. RBAC:
 * `iam:permission:update`. Body estrito (0111 §3.2): SÓ `is_sensitive`,
 * boolean JSON — chave extra é 422.
 */
export async function setPermissionSensitivity(permissionId, isSensitive) {
   const response = await axios.put(
      `/permissions/${permissionId}`,
      { is_sensitive: Boolean(isSensitive) },
      ACTION_CONFIG
   );
   const body = response?.data;
   const updated = body?.data?.permission ?? body?.permission ?? body;
   return updated && typeof updated === 'object'
      ? normalizePermission(updated)
      : null;
}
